import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Building2, 
  Users, 
  Shield, 
  BarChart3, 
  CheckCircle2, 
  ArrowRight,
  Send,
  Globe,
  Target,
  Briefcase,
  MapPin,
  UserPlus,
  Rocket
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

export default function EnterprisePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    company_size: "",
    industry: "",
    needs: "",
    budget_range: "",
    service_type: "", // "consultations" or "gtm_team" or "both"
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.contact_name || !formData.email || !formData.needs) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/enterprise/inquiry`, formData);
      setSubmitted(true);
      toast.success("Inquiry submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Users,
      title: "Dedicated Expert Pools",
      description: "Access curated networks of experts specifically matched to your industry and needs."
    },
    {
      icon: Shield,
      title: "Compliance & Security",
      description: "Enterprise-grade security with custom NDA workflows and compliance tracking."
    },
    {
      icon: BarChart3,
      title: "Analytics & ROI",
      description: "Track engagement, measure impact, and demonstrate clear ROI on expert consultations."
    },
    {
      icon: Building2,
      title: "Account Management",
      description: "Dedicated account managers to help you get the most from the platform."
    }
  ];

  const logos = [
    "Fortune 500 Companies",
    "PE & VC Firms", 
    "Consulting Firms",
    "Growth Stage Startups"
  ];

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1920&q=80"
            alt="Enterprise"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/90 to-obsidian" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-500 mb-4">EXPERTBASE FOR ENTERPRISE</p>
            <h1 className="font-display text-5xl md:text-6xl mb-6">
              Expert Intelligence<br />at Scale
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              Give your entire organization access to world-class expertise. Custom networks, 
              enterprise security, and dedicated support for teams that demand the best.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 border-y border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="p-8 bg-charcoal border border-slate-800 rounded-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <feature.icon className="w-10 h-10 text-gold-500 mb-6" />
                <h3 className="font-display text-xl mb-3">{feature.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted By */}
      <section className="py-16 bg-charcoal/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-slate-500 mb-8">TRUSTED BY LEADING ORGANIZATIONS</p>
          <div className="flex flex-wrap justify-center gap-12">
            {logos.map((logo, index) => (
              <span key={index} className="text-slate-600 text-lg font-medium">
                {logo}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Two Services Section - Side by Side */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-500 mb-4">OUR SERVICES</p>
            <h2 className="font-display text-4xl md:text-5xl mb-6">
              Two Ways to Accelerate<br />Your Growth
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Whether you need strategic insights or a full team on the ground, we've got you covered.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Service 1: Expert Consultations */}
            <motion.div
              className="bg-charcoal border border-slate-800 rounded-sm overflow-hidden h-full flex flex-col"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="p-8 border-b border-slate-800 bg-gradient-to-r from-blue-500/5 to-transparent">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl">Expert Consultations</h3>
                    <p className="text-slate-500 text-sm">Learn from those who've been there</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8 flex-1">
                <p className="text-slate-400 mb-6 leading-relaxed">
                  Book 1-on-1 sessions with industry veterans to gain insights, validate strategies, 
                  and learn from real-world experience in your target markets.
                </p>
                
                <div className="space-y-4 mb-8">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500">What You Get</h4>
                  {[
                    { icon: Target, text: "Strategic advice on market entry & expansion" },
                    { icon: Globe, text: "Local market insights & competitive landscape" },
                    { icon: Shield, text: "Regulatory guidance & compliance know-how" },
                    { icon: BarChart3, text: "Pricing strategies & business model validation" },
                    { icon: Users, text: "Network introductions & partnership leads" },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <item.icon className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300 text-sm">{item.text}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-obsidian border border-slate-800 p-4 rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-400 text-sm">Starting from</span>
                    <span className="text-2xl font-display text-white">$250<span className="text-sm text-slate-500">/session</span></span>
                  </div>
                  <p className="text-xs text-slate-500">Volume discounts available for enterprise accounts</p>
                </div>
              </div>

              <div className="p-8 pt-0">
                <a href="#contact">
                  <Button 
                    className="w-full bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 font-semibold py-5 rounded-sm"
                    onClick={() => setFormData(prev => ({ ...prev, service_type: "consultations" }))}
                  >
                    Book Expert Sessions
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Service 2: GTM Team Building */}
            <motion.div
              className="bg-charcoal border border-slate-800 rounded-sm overflow-hidden h-full flex flex-col"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="p-8 border-b border-slate-800 bg-gradient-to-r from-gold-500/10 to-transparent">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gold-500/20 rounded-full flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-gold-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-2xl">GTM Team Building</h3>
                      <span className="text-xs bg-gold-500/20 text-gold-500 px-2 py-0.5 rounded-full">NEW</span>
                    </div>
                    <p className="text-slate-500 text-sm">Build your local presence</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8 flex-1">
                <p className="text-slate-400 mb-6 leading-relaxed">
                  Go beyond advice. We help you identify, vet, and hire the right people 
                  to build your local team and execute your go-to-market strategy.
                </p>
                
                <div className="space-y-4 mb-8">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500">What You Get</h4>
                  {[
                    { icon: Briefcase, text: "Executive search: Country Managers, Directors, C-Suite" },
                    { icon: UserPlus, text: "Commercial team hiring: Sales, BD, Account Management" },
                    { icon: MapPin, text: "Local ops setup: Finance, Legal, HR, Customer Success" },
                    { icon: Target, text: "GTM strategy development & market entry planning" },
                    { icon: Globe, text: "On-ground support in UAE, Saudi, India, Singapore & more" },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <item.icon className="w-5 h-5 text-gold-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300 text-sm">{item.text}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-obsidian border border-slate-800 p-4 rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-400 text-sm">Engagement starts at</span>
                    <span className="text-2xl font-display text-white">$5,000<span className="text-sm text-slate-500">/month</span></span>
                  </div>
                  <p className="text-xs text-slate-500">Custom packages based on scope and region</p>
                </div>
              </div>

              <div className="p-8 pt-0">
                <a href="#contact">
                  <Button 
                    className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-5 rounded-sm"
                    onClick={() => setFormData(prev => ({ ...prev, service_type: "gtm_team" }))}
                  >
                    Discuss GTM Strategy
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>

          {/* Focus Markets */}
          <motion.div 
            className="mt-16 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-slate-500 mb-4">CURRENT FOCUS MARKETS</p>
            <div className="flex flex-wrap justify-center gap-3">
              {["UAE", "Saudi Arabia", "Egypt", "India", "Singapore", "UK", "USA"].map((market, index) => (
                <span 
                  key={index}
                  className="px-4 py-2 bg-charcoal border border-slate-800 rounded-full text-sm text-slate-300"
                >
                  {market}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 bg-charcoal/30" id="contact">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-500 mb-4">GET STARTED</p>
              <h2 className="font-display text-4xl md:text-5xl mb-6">
                Let's Talk About<br />Your Goals
              </h2>
              <p className="text-xl text-slate-400 mb-8 leading-relaxed">
                Whether you need expert insights or a full go-to-market team, 
                we'll create a solution tailored to your needs.
              </p>

              <ul className="space-y-4">
                {[
                  "Custom solutions for both services",
                  "Onboarding within 48 hours",
                  "Dedicated account management",
                  "Flexible engagement models"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle2 className="w-5 h-5 text-gold-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {submitted ? (
                <div className="bg-charcoal border border-slate-800 p-12 rounded-sm text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="font-display text-2xl mb-4">Thank You!</h3>
                  <p className="text-slate-400 mb-6">
                    We've received your inquiry. Our team will be in touch within 24 hours.
                  </p>
                  <Button 
                    onClick={() => navigate("/experts")}
                    className="bg-gold-500 hover:bg-gold-400 text-black"
                  >
                    Browse Experts
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-charcoal border border-slate-800 p-8 rounded-sm space-y-6">
                  {/* Service Type Selection */}
                  <div className="space-y-3">
                    <Label>What are you interested in? *</Label>
                    <div className="grid grid-cols-1 gap-3">
                      {[
                        { value: "consultations", label: "Expert Consultations", desc: "1-on-1 sessions with industry experts", color: "blue" },
                        { value: "gtm_team", label: "GTM Team Building", desc: "Build your local presence & team", color: "gold" },
                        { value: "both", label: "Both Services", desc: "Full-service market entry support", color: "purple" },
                      ].map((option) => (
                        <div
                          key={option.value}
                          onClick={() => setFormData(prev => ({ ...prev, service_type: option.value }))}
                          className={`p-4 rounded-sm border cursor-pointer transition-all ${
                            formData.service_type === option.value
                              ? option.color === "blue" 
                                ? "bg-blue-500/10 border-blue-500"
                                : option.color === "gold"
                                  ? "bg-gold-500/10 border-gold-500"
                                  : "bg-purple-500/10 border-purple-500"
                              : "bg-obsidian border-slate-700 hover:border-slate-600"
                          }`}
                          data-testid={`service-${option.value}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={`font-medium ${
                                formData.service_type === option.value 
                                  ? option.color === "blue" ? "text-blue-400" : option.color === "gold" ? "text-gold-400" : "text-purple-400"
                                  : "text-white"
                              }`}>{option.label}</p>
                              <p className="text-xs text-slate-500">{option.desc}</p>
                            </div>
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              formData.service_type === option.value
                                ? option.color === "blue" 
                                  ? "border-blue-500 bg-blue-500"
                                  : option.color === "gold"
                                    ? "border-gold-500 bg-gold-500"
                                    : "border-purple-500 bg-purple-500"
                                : "border-slate-600"
                            }`}>
                              {formData.service_type === option.value && (
                                <CheckCircle2 className="w-3 h-3 text-black" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Company Name *</Label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                        className="bg-obsidian border-slate-700"
                        placeholder="Acme Inc."
                        data-testid="enterprise-company"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Your Name *</Label>
                      <Input
                        value={formData.contact_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                        className="bg-obsidian border-slate-700"
                        placeholder="John Doe"
                        data-testid="enterprise-name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Work Email *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="bg-obsidian border-slate-700"
                        placeholder="john@acme.com"
                        data-testid="enterprise-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="bg-obsidian border-slate-700"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Company Size</Label>
                      <Select 
                        value={formData.company_size}
                        onValueChange={(v) => setFormData(prev => ({ ...prev, company_size: v }))}
                      >
                        <SelectTrigger className="bg-obsidian border-slate-700" data-testid="enterprise-size">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent className="bg-charcoal border-slate-700">
                          <SelectItem value="1-50">1-50 employees</SelectItem>
                          <SelectItem value="51-200">51-200 employees</SelectItem>
                          <SelectItem value="201-1000">201-1000 employees</SelectItem>
                          <SelectItem value="1000+">1000+ employees</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Target Market / Region</Label>
                      <Select 
                        value={formData.industry}
                        onValueChange={(v) => setFormData(prev => ({ ...prev, industry: v }))}
                      >
                        <SelectTrigger className="bg-obsidian border-slate-700" data-testid="enterprise-industry">
                          <SelectValue placeholder="Select region" />
                        </SelectTrigger>
                        <SelectContent className="bg-charcoal border-slate-700">
                          <SelectItem value="UAE">UAE</SelectItem>
                          <SelectItem value="Saudi Arabia">Saudi Arabia</SelectItem>
                          <SelectItem value="MENA">MENA Region</SelectItem>
                          <SelectItem value="India">India</SelectItem>
                          <SelectItem value="Singapore">Singapore</SelectItem>
                          <SelectItem value="Southeast Asia">Southeast Asia</SelectItem>
                          <SelectItem value="UK">United Kingdom</SelectItem>
                          <SelectItem value="USA">United States</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Tell us about your goals *</Label>
                    <Textarea
                      value={formData.needs}
                      onChange={(e) => setFormData(prev => ({ ...prev, needs: e.target.value }))}
                      className="bg-obsidian border-slate-700 min-h-[120px]"
                      placeholder={
                        formData.service_type === "consultations" 
                          ? "What insights are you looking for? Which markets or topics interest you?"
                          : formData.service_type === "gtm_team"
                            ? "Which market are you expanding to? What roles do you need to fill?"
                            : "Tell us about your expansion plans, target markets, and what you're hoping to achieve..."
                      }
                      data-testid="enterprise-needs"
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={loading || !formData.service_type}
                    className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-6 rounded-sm"
                    data-testid="enterprise-submit"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Inquiry
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
